// src/hooks/useImageUploadZone.ts
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useToast } from "../components/Toast";
import { filterImageFiles, getFilesFromDataTransfer } from "../utils/imageFiles";
import {
  setActiveZone,
  clearActiveZone,
  subscribePaste,
  type PasteZone,
} from "./imagePasteManager";

export interface UseImageUploadZoneOptions {
  onFiles: (images: File[]) => void | Promise<void>;
  multiple?: boolean;
  maxFiles?: number; // 仅 multiple 时生效：可接收的最大张数（=剩余名额）。超出→截断并 toast.warning。
  disabled?: boolean;
}

export interface ImageUploadZoneProps {
  onDragEnter: (e: React.DragEvent) => void;
  onDragOver: (e: React.DragEvent) => void;
  onDragLeave: (e: React.DragEvent) => void;
  onDrop: (e: React.DragEvent) => void;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
  title: string;
}

export interface UseImageUploadZoneResult {
  isOver: boolean;
  zoneProps: ImageUploadZoneProps;
}

export function useImageUploadZone(
  options: UseImageUploadZoneOptions,
): UseImageUploadZoneResult {
  const { t } = useTranslation();
  const toast = useToast();
  const [isOver, setIsOver] = useState(false);
  const dragDepth = useRef(0);

  // 每渲染同步最新 options，回调一律读 latestRef，杜绝旧闭包
  const latestRef = useRef(options);
  latestRef.current = options;

  // 共享核心：过滤 + toast + 安全启动上传。返回是否消费了图片。
  const processFiles = useCallback(
    (rawFiles: File[]): boolean => {
      const { onFiles, multiple, maxFiles, disabled } = latestRef.current;
      if (disabled) return false;
      const { images, rejectedCount } = filterImageFiles(rawFiles);
      if (rejectedCount > 0) toast.error(t("dragImageOnly", { ns: "common" }));
      if (images.length === 0) return false;
      let selected: File[];
      if (!multiple) {
        selected = images.slice(0, 1); // 单图位：静默取第一张（无超额提示）
      } else if (typeof maxFiles === "number") {
        selected = images.slice(0, Math.max(0, maxFiles));
        if (images.length > maxFiles) toast.warning(t("imageLimitReached", { ns: "common" }));
      } else {
        selected = images; // 多图位未给 maxFiles：不设上限
      }
      if (selected.length > 0) {
        void Promise.resolve(onFiles(selected)).catch((err: unknown) =>
          toast.error((err instanceof Error && err.message) || t("uploadFailed", { ns: "common" })),
        );
      }
      return true; // 有图片即视为消费（已上传或已提示超额），供 paste 决定 preventDefault
    },
    [t, toast],
  );

  // 注册给 manager 的稳定 zone：回调引用稳定且读 latestRef（经 processFiles）
  const zoneRef = useRef<PasteZone>({ handleClipboardFiles: processFiles });
  zoneRef.current.handleClipboardFiles = processFiles;

  useEffect(() => {
    const unsubscribe = subscribePaste();
    return () => {
      // 卸载时先清掉残留 active zone：若本位悬停后因名额满/modal 关闭/状态切换而卸载，
      // onMouseLeave 不一定触发；只要其他上传位仍订阅、document 监听就还在，
      // 旧 zone 会继续吃 paste。必须先 clear 再 unsubscribe。
      clearActiveZone(zoneRef.current);
      unsubscribe();
    };
  }, []);

  const onDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    dragDepth.current += 1;
    if (!latestRef.current.disabled) setIsOver(true);
  }, []);

  const onDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = latestRef.current.disabled ? "none" : "copy";
  }, []);

  const onDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    dragDepth.current -= 1;
    if (dragDepth.current <= 0) {
      dragDepth.current = 0;
      setIsOver(false);
    }
  }, []);

  const onDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault(); // 含 disabled：防浏览器打开拖入文件
      dragDepth.current = 0;
      setIsOver(false);
      processFiles(getFilesFromDataTransfer(e.dataTransfer));
    },
    [processFiles],
  );

  const onMouseEnter = useCallback(() => setActiveZone(zoneRef.current), []);
  const onMouseLeave = useCallback(() => clearActiveZone(zoneRef.current), []);

  return {
    isOver,
    zoneProps: {
      onDragEnter,
      onDragOver,
      onDragLeave,
      onDrop,
      onMouseEnter,
      onMouseLeave,
      title: t("uploadZoneHint", { ns: "common" }),
    },
  };
}
